import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Paper,
  Grid,
  OutlinedInput,
  Collapse,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const monthMap: { [key: string]: string } = {
  Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
  Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
};

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const YearMonthPicker = (props: any) => {
  const {
    setCustomMonth,
    setFilterMonth,
    handleChange,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2025);
  const [displayMonth, setDisplayMonth] = useState<string | null>('0M');
  const [isAllMonths, setIsAllMonths] = useState(false);

  const NAVY_BLUE = '#003366';
  const CYAN_BLUE = '#1da1d2';

  // Resets the month selection state when the year changes
  const handleYearChange = (newYear: number) => {
    setSelectedYear(newYear);
    setIsAllMonths(false);
    setDisplayMonth(null); // Clears the highlights in the grid
    // No callback triggered here, waiting for month or "All Months" click
  };

  const handleMonthClick = (monthName: string) => {
    const formattedDate = `${selectedYear}-${monthMap[monthName]}`;
    setDisplayMonth(monthName);
    setIsAllMonths(false);
    setIsOpen(false);

    // Trigger callbacks with YYYY-MM
    handleChange(formattedDate);
    setFilterMonth(formattedDate);
    setCustomMonth(formattedDate);
  };

  const handleAllMonthsClick = () => {
    const formattedYear = `${selectedYear}`;
    setIsAllMonths(true);
    setDisplayMonth(null);
    setIsOpen(false);

    // Trigger callbacks with YYYY
    handleChange(formattedYear);
    setFilterMonth(formattedYear);
    setCustomMonth(formattedYear);
  };
  // Logic to set previous month on mount
  useEffect(() => {
    const now = new Date();
    let year = now.getFullYear();
    let monthIndex = now.getMonth() - 1; // getMonth() is 0-indexed

    // Handle January edge case (go back to Dec of previous year)
    if (monthIndex < 0) {
      monthIndex = 11;
      year = year - 1;
    }

    const prevMonthName = months[monthIndex];
    const prevMonthValue = monthMap[prevMonthName];
    const formattedDate = `${year}-${prevMonthValue}`;

    // Update Local State
    setSelectedYear(year);
    setDisplayMonth(prevMonthName);

    // Update External States
    handleChange(formattedDate);
    setFilterMonth(formattedDate);
    setCustomMonth(formattedDate);

  }, []);
  return (
    <Box sx={{ width: 350 }}>
      {/* HEADER SECTION */}
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          bgcolor: NAVY_BLUE,
          color: 'white',
          p: '12px 16px',
          borderRadius: isOpen ? '4px 4px 0 0' : '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'border-radius 0.3s',
          '&:hover': { opacity: 0.95 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
            {isAllMonths ? `${selectedYear}, All Months` :
              displayMonth ? `${displayMonth}, ${selectedYear}` : `Select Month, ${selectedYear}`}
          </Typography>
          {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </Box>
        <CalendarMonthIcon />
      </Box>

      {/* PICKER BODY */}
      <Collapse in={isOpen}>
        <Paper
          elevation={4}
          sx={{
            p: 4,
            bgcolor: 'white !important',
            borderRadius: '0 0 4px 4px',
            border: `1px solid ${NAVY_BLUE}`,
            borderTop: 'none',
            margin: '0px !important',
            position: 'absolute',
            zIndex: 1,
            width: '350px !important'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Select
              value={selectedYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
              size="small"
              input={<OutlinedInput sx={{ borderRadius: '8px' }} />}
              sx={{
                width: 100,
                bgcolor: CYAN_BLUE,
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: CYAN_BLUE }
              }}
            >
              {Array.from(
                { length: new Date().getFullYear() - 2020 + 1 },
                (_, index) => 2023 + index
              ).map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>

            <Button
              onClick={handleAllMonthsClick}
              disabled={isAllMonths}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: isAllMonths ? '#ccc' : CYAN_BLUE
              }}
            >
              All Months
            </Button>
          </Box>

          <Grid container sx={{ border: '1px solid #eef2f6' }}>
            {months.map((month) => {
              const isActive = !isAllMonths && displayMonth === month;
              return (
                <Grid item xs={4} key={month} sx={{ border: '0.5px solid #eef2f6' }}>
                  <Box
                    onClick={() => handleMonthClick(month)}
                    sx={{
                      height: 55,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      bgcolor: isActive ? CYAN_BLUE : 'transparent',
                      color: isActive ? 'white' : '#666',
                      '&:hover': { bgcolor: isActive ? CYAN_BLUE : '#f5f5f5' }
                    }}
                  >
                    {month}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default YearMonthPicker;